import React, {useState, useContext, useEffect} from 'react';
import {FaEye, FaEyeSlash} from "react-icons/fa";
import {authAPI} from "../../../../api/authApi";
import { useToast } from '../../../../context/ToastContext';
import AuthContext from '../../../../context/AuthContext';
import LanguageContext from '../../../../context/LanguageContext';
import Button from "../../../../components/UI/Button/Button";
import './changePassword.css';
import {useTranslation} from "react-i18next";
import i18n from '../../../../i18n';
import {
    PASSWORD_SETUP_CODE_LENGTH,
    getPasswordSetupErrorDetails,
    getPasswordSetupErrorMessage,
    usePasswordSetupCode,
} from '../../../../utils/passwordSetup';

// ==================== VALIDATION HELPERS ====================
const validatePassword = (password) => {
    const errors = [];
    if (!password) {
        errors.push(i18n.t('profilePage.passwordRequired'));
    } else {
        if (password.length < 8) errors.push(i18n.t('profilePage.minCharacters'));
        if (!/[A-Z]/.test(password)) errors.push(i18n.t('profilePage.uppercase'));
        if (!/[a-z]/.test(password)) errors.push(i18n.t('profilePage.lowercase'));
        if (!/\d/.test(password)) errors.push(i18n.t('profilePage.number'));
        if (!/[!@#$%^&*]/.test(password)) errors.push(i18n.t('profilePage.specialChar'));
    }

    return errors;
};

const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*]/.test(password)) strength++;

    return strength;
};

const EMPTY_FORM = {currentPassword: '', code: '', newPassword: '', confirmNewPassword: ''};

/**
 * Change password, or — for Google-only customers who never had one — set a first password
 * with a 6-digit code emailed to them (no "current password" to ask for).
 */
function ChangePassword() {
    const { dir } = useContext(LanguageContext);
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const { addToast } = useToast();

    // Change password form state
    const [passwordForm, setPasswordForm] = useState(EMPTY_FORM);
    const [passwordErrors, setPasswordErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [passwordVisibility, setPasswordVisibility] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [passwordStrength, setPasswordStrength] = useState(0);

    const {user} = useContext(AuthContext);

    // null until the profile loads; false = Google-only account with no password yet
    const [hasPassword, setHasPassword] = useState(null);
    const setupMode = hasPassword === false;
    const { codeSent, maskedEmail, sending, cooldown, sendCode } = usePasswordSetupCode(
        authAPI.requestPasswordSetupCode,
    );

    useEffect(() => {
        let active = true;
        authAPI.getUserData()
            .then((response) => {
                const profile = response?.data?.data ?? response?.data;
                if (active) setHasPassword(profile?.hasPassword !== false);
            })
            .catch(() => {
                if (active) setHasPassword(true);
            });
        return () => {
            active = false;
        };
    }, []);

    // ==================== CHANGE PASSWORD ====================
    const handlePasswordChange = (e) => {
        const {name, value} = e.target;
        const nextValue = name === 'code'
            ? value.replace(/\D/g, '').slice(0, PASSWORD_SETUP_CODE_LENGTH)
            : value;
        setPasswordForm(prev => ({...prev, [name]: nextValue}));
        setFormError('');

        if (name === 'newPassword') {
            setPasswordStrength(getPasswordStrength(value));
        }

        if (passwordErrors[name]) {
            setPasswordErrors(prev => ({...prev, [name]: ''}));
        }
    };

    const togglePasswordVisibility = (field) => {
        setPasswordVisibility(prev => ({...prev, [field]: !prev[field]}));
    };

    const validatePasswordForm = () => {
        const errors = {};

        if (setupMode) {
            if (passwordForm.code.length !== PASSWORD_SETUP_CODE_LENGTH) {
                errors.code = t('profilePage.setPassword.errors.codeRequired');
            }
        } else if (!passwordForm.currentPassword) {
            errors.currentPassword = t('profilePage.allFieldsRequired');
        }

        const newPasswordErrors = validatePassword(passwordForm.newPassword);
        if (newPasswordErrors.length > 0) {
            errors.newPassword = newPasswordErrors[0];
        }

        if (passwordForm.newPassword !== passwordForm.confirmNewPassword) {
            errors.confirmNewPassword = t('profilePage.passwordMismatch');
        }

        return errors;
    };

    const handleSendCode = async () => {
        setFormError('');
        try {
            await sendCode();
        } catch (err) {
            setFormError(getPasswordSetupErrorMessage(err, t, 'profilePage.setPassword.errors.sendFailed'));
        }
    };

    const handleChangePasswordSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        const errors = validatePasswordForm();

        if (Object.keys(errors).length > 0) {
            setPasswordErrors(errors);
            return;
        }

        setLoading(true);
        try {
            if (setupMode) {
                await authAPI.setupPassword({
                    code: passwordForm.code,
                    newPassword: passwordForm.newPassword,
                    confirmPassword: passwordForm.confirmNewPassword,
                });
                addToast(t('profilePage.setPassword.success'), 'success');
                setHasPassword(true);
            } else {
                const response = await authAPI.updateProfile(user._id, {
                    currentPassword: passwordForm.currentPassword,
                    newPassword: passwordForm.newPassword
                });
                addToast(response.data.message || t('profilePage.passwordSuccess'), 'success');
            }
            setPasswordForm(EMPTY_FORM);
            setPasswordErrors({});
            setPasswordStrength(0);
        } catch (err) {
            if (setupMode) {
                setFormError(getPasswordSetupErrorMessage(err, t, 'profilePage.setPassword.errors.setFailed'));
            } else {
                // Account has no password (Google sign-up): switch to the set-password flow.
                if (getPasswordSetupErrorDetails(err).code === 'PASSWORD_NOT_SET') {
                    setHasPassword(false);
                }
                setFormError(err.response?.data?.message || t('profilePage.passwordUpdateFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const showPasswordFields = !setupMode || codeSent;

    return (
        <>
            <form
                className='change-password-form'
                onSubmit={handleChangePasswordSubmit}
                noValidate
                dir={dir()}
            >
                <h2 className='change-password-form-title'>
                    {setupMode ? t('profilePage.setPassword.title') : t('profilePage.changePasswordTitle')}
                </h2>

                {setupMode && (
                    <p className='change-password-setup-text' role='status'>
                        {codeSent
                            ? t('profilePage.setPassword.codeSent', {email: maskedEmail})
                            : t('profilePage.setPassword.description')}
                    </p>
                )}

                <div className='change-password-form-content'>
                    {setupMode ? (
                        codeSent && (
                            <div className='change-password-form-box'>
                                <label className='change-password-form-label' htmlFor='setup-code'>
                                    {t('profilePage.setPassword.codeLabel')}
                                </label>
                                <input
                                    id='setup-code'
                                    className={`change-password-form-input ${passwordErrors.code ? 'change-password-form-input-error' : ''}`}
                                    type='text'
                                    inputMode='numeric'
                                    autoComplete='one-time-code'
                                    dir='ltr'
                                    maxLength={PASSWORD_SETUP_CODE_LENGTH}
                                    name='code'
                                    value={passwordForm.code}
                                    onChange={handlePasswordChange}
                                    aria-invalid={!!passwordErrors.code}
                                    aria-describedby={passwordErrors.code ? 'code-error' : undefined}
                                />
                                {passwordErrors.code && (
                                    <span id='code-error' className='change-password-form-error' role='alert'>
                                        {passwordErrors.code}
                                    </span>
                                )}
                                <Button
                                    variant='link'
                                    type='button'
                                    className='change-password-resend'
                                    onClick={handleSendCode}
                                    disabled={sending || cooldown > 0}
                                >
                                    {cooldown > 0
                                        ? t('profilePage.setPassword.resendIn', {count: cooldown})
                                        : t('profilePage.setPassword.resend')}
                                </Button>
                            </div>
                        )
                    ) : (
                    /* Current Password Field */
                    <div className='change-password-form-box'>
                        <label className='change-password-form-label'>{t('profilePage.currentPassword')}</label>
                        <div className='change-password-password-wrapper'>
                            <input
                                className={`change-password-form-input ${passwordErrors.currentPassword ? 'change-password-form-input-error' : ''}`}
                                type={passwordVisibility.current ? 'text' : 'password'}
                                name='currentPassword'
                                value={passwordForm.currentPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('profilePage.currentPassword')}
                                aria-invalid={!!passwordErrors.currentPassword}
                                aria-describedby={passwordErrors.currentPassword ? 'current-error' : undefined}
                            />
                            <button
                                type='button'
                                className='change-password-password-toggle'
                                onClick={() => togglePasswordVisibility('current')}
                                aria-label='Toggle password visibility'
                            >
                                {passwordVisibility.current ? <FaEyeSlash/> : <FaEye/>}
                            </button>
                        </div>
                        {passwordErrors.currentPassword && (
                            <span id='current-error' className='change-password-form-error' role='alert'>
                      {passwordErrors.currentPassword}
                    </span>
                        )}
                    </div>
                    )}

                    {showPasswordFields && (
                    <>
                    {/* New Password Field */}
                    <div className='change-password-form-box'>
                        <label className='change-password-form-label'>{t('profilePage.newPassword')}</label>
                        <div className='change-password-password-wrapper'>
                            <input
                                className={`change-password-form-input ${passwordErrors.newPassword ? 'change-password-form-input-error' : ''}`}
                                type={passwordVisibility.new ? 'text' : 'password'}
                                name='newPassword'
                                value={passwordForm.newPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('profilePage.newPassword')}
                                autoComplete='new-password'
                                aria-invalid={!!passwordErrors.newPassword}
                                aria-describedby={passwordErrors.newPassword ? 'new-error' : undefined}
                            />
                            <button
                                type='button'
                                className='change-password-password-toggle'
                                onClick={() => togglePasswordVisibility('new')}
                                aria-label='Toggle password visibility'
                            >
                                {passwordVisibility.new ? <FaEyeSlash/> : <FaEye/>}
                            </button>
                        </div>
                        {passwordErrors.newPassword && (
                            <span id='new-error' className='change-password-form-error' role='alert'>
                      {passwordErrors.newPassword}
                    </span>
                        )}

                        {/* Password Strength Indicator */}
                        {passwordForm.newPassword && (
                            <div className='change-password-password-strength'>
                                <div className='change-password-strength-bar'>
                                    <div
                                        className={`change-password-strength-fill change-password-strength-${passwordStrength}`}
                                        style={{width: `${(passwordStrength / 4) * 100}%`}}
                                    ></div>
                                </div>
                                <span className='change-password-strength-label'>
                                    {t('profilePage.' + (passwordStrength >= 3 ? 'strong' : ['weak', 'fair', 'good'][passwordStrength]))}
                                  </span>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div className='change-password-form-box'>
                        <label className='change-password-form-label'>{t('profilePage.confirmPassword')}</label>
                        <div className='change-password-password-wrapper'>
                            <input
                                className={`change-password-form-input ${passwordErrors.confirmNewPassword ? 'change-password-form-input-error' : ''}`}
                                type={passwordVisibility.confirm ? 'text' : 'password'}
                                name='confirmNewPassword'
                                value={passwordForm.confirmNewPassword}
                                onChange={handlePasswordChange}
                                placeholder={t('profilePage.confirmPassword')}
                                autoComplete='new-password'
                                aria-invalid={!!passwordErrors.confirmNewPassword}
                                aria-describedby={passwordErrors.confirmNewPassword ? 'confirm-error' : undefined}
                            />
                            <button
                                type='button'
                                className='change-password-password-toggle'
                                onClick={() => togglePasswordVisibility('confirm')}
                                aria-label='Toggle password visibility'
                            >
                                {passwordVisibility.confirm ? <FaEyeSlash/> : <FaEye/>}
                            </button>
                        </div>
                        {passwordErrors.confirmNewPassword && (
                            <span id='confirm-error' className='change-password-form-error' role='alert'>
                      {passwordErrors.confirmNewPassword}
                    </span>
                        )}
                    </div>
                    </>
                    )}
                </div>

                {showPasswordFields && (
                <p className='change-password-password-requirements'>
                    {t('profilePage.passwordRequirements')}
                </p>
                )}

                <div className='change-password-actions'>
                    {setupMode && !codeSent ? (
                        <Button
                            className='change-password-btn'
                            type='button'
                            onClick={handleSendCode}
                            disabled={sending || cooldown > 0}
                            aria-busy={sending}
                        >
                            {sending ? t('profilePage.setPassword.sending') : t('profilePage.setPassword.sendCode')}
                        </Button>
                    ) : (
                    <Button
                        className='change-password-btn'
                        type='submit'
                        disabled={loading}
                        aria-busy={loading}
                    >
                        {loading
                            ? t('profilePage.updating')
                            : setupMode ? t('profilePage.setPassword.submit') : t('profilePage.update')}
                    </Button>
                    )}
                </div>

                {formError && (
                    <p className='change-password-form-error change-password-form-error--form' role='alert'>
                        {formError}
                    </p>
                )}
            </form>
        </>
    )
}

export default ChangePassword;

